import { useEffect, useRef, useState, type ElementType } from 'react'
import { useFieldArray, useForm, type Path, type UseFormReturn } from 'react-hook-form'
import { z } from 'zod'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router'
import { toast } from 'sonner'
import { Camera, Check, FileText, Info, Loader2, Plus, Smartphone, Upload } from 'lucide-react'
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { GoogleAddressAutocomplete } from '@/components/GoogleAddressAutocomplete'
import { UpdateCardDialog } from '@/components/manager/subscription/UpdateCardDialog'
import { useEstDesktop } from '@/hooks/useEstDesktop'
import { cn } from '@/lib/utils'
import {
  downloadPortLetter,
  portErrorOf,
  useCreatePhoneSignCode,
  useSavePortRequest,
  useSignPortRequest,
  useStorePortRequest,
  useUploadPortInvoice,
  useUploadSignedLetter,
  type PortOptions,
  type PortRequest,
  type PortRequestInput,
  type PortScope,
} from '@/api/portage'
import { PhoneSignPanel } from './PhoneSignPanel'
import { SignaturePad, type SignaturePadHandle } from './SignaturePad'
import {
  formatNanp,
  formatPostal,
  isTollFree,
  PO_BOX_RE,
  POSTAL_RE,
  provinceLabel,
  toE164,
} from './portageUtils'

// =============================================================================
// Form model
// =============================================================================

interface NumberRow {
  phone_number: string
  is_mobile: boolean
  /** Carrier, account number and PIN differ from the defaults for this number. */
  custom: boolean
  carrier: string
  account_number: string
  pin: string
  /** Stored on the server (never sent back): an empty field keeps it. */
  account_number_set: boolean
  pin_set: boolean
}

interface Values {
  numbers: NumberRow[]
  scope: PortScope
  carrier: string
  account_number: string
  pin: string
  temporary_number_id: number | null
  first_name: string
  last_name: string
  business_name: string
  address: string
  address2: string
  city: string
  province: string
  postal_code: string
  signer_name: string
  signer_title: string
  signer_email: string
  check_owner: boolean
  check_matches_bill: boolean
  check_authorize: boolean
  check_electronic: boolean
}

type ScreenId = 'numbers' | 'carrier' | 'holder' | 'address' | 'invoice' | 'signer' | 'checks' | 'sign'
type StepKey = 'numbers' | 'holder' | 'invoice' | 'signature'

const STEPS: { key: StepKey; screens: ScreenId[] }[] = [
  { key: 'numbers', screens: ['numbers', 'carrier'] },
  { key: 'holder', screens: ['holder', 'address'] },
  { key: 'invoice', screens: ['invoice'] },
  { key: 'signature', screens: ['signer', 'checks', 'sign'] },
]
const SCREENS: ScreenId[] = STEPS.flatMap((s) => s.screens)
const stepOf = (screen: ScreenId) => STEPS.findIndex((s) => s.screens.includes(screen))

const emptyRow = (): NumberRow => ({
  phone_number: '',
  is_mobile: false,
  custom: false,
  carrier: '',
  account_number: '',
  pin: '',
  account_number_set: false,
  pin_set: false,
})

function initialValues(transfer: PortRequest | null, options: PortOptions | undefined, email: string): Values {
  const numbers = transfer?.numbers.length
    ? transfer.numbers.map((n) => ({
        ...emptyRow(),
        phone_number: formatNanp(n.phone_number),
        is_mobile: n.line_type === 'cellulaire',
        carrier: n.carrier,
        account_number_set: n.account_number_set,
        pin_set: n.pin_set,
      }))
    : [emptyRow()]
  // One carrier for every number: it becomes the default; several: each number keeps its own.
  const carriers = Array.from(new Set(numbers.map((n) => n.carrier).filter(Boolean)))
  const sameCarrier = carriers.length <= 1
  return {
    numbers: numbers.map((n) => ({ ...n, custom: !sameCarrier })),
    scope: transfer?.scope ?? 'tous',
    carrier: sameCarrier ? carriers[0] ?? '' : '',
    account_number: '',
    pin: '',
    temporary_number_id: transfer?.temporary_number?.id ?? options?.default_temporary_number_id ?? null,
    first_name: transfer?.first_name ?? '',
    last_name: transfer?.last_name ?? '',
    business_name: transfer?.business_name ?? '',
    address: transfer?.address ?? '',
    address2: transfer?.address2 ?? '',
    city: transfer?.city ?? '',
    province: transfer?.province ?? '',
    postal_code: transfer?.postal_code ?? '',
    signer_name: transfer?.signer_name || '',
    signer_title: transfer?.signer_title ?? '',
    signer_email: transfer?.signer_email || email,
    check_owner: false,
    check_matches_bill: false,
    check_authorize: false,
    check_electronic: false,
  }
}

/** What the server receives: defaults copied onto every number unless it has its own. */
function toInput(v: Values): PortRequestInput {
  return {
    scope: v.scope,
    temporary_number_id: v.temporary_number_id,
    first_name: v.first_name.trim(),
    last_name: v.last_name.trim(),
    business_name: v.business_name.trim(),
    address: v.address.trim(),
    address2: v.address2.trim(),
    city: v.city.trim(),
    province: v.province,
    postal_code: v.postal_code.trim(),
    signer_name: v.signer_name.trim(),
    signer_title: v.signer_title.trim(),
    signer_email: v.signer_email.trim(),
    numbers: v.numbers
      .filter((n) => n.phone_number.trim())
      .map((n) => {
        const carrier = (n.custom ? n.carrier : v.carrier).trim()
        const account = (n.custom ? n.account_number : v.account_number).trim()
        const pin = (n.custom ? n.pin : v.pin).trim()
        const tollFree = isTollFree(n.phone_number)
        return {
          phone_number: toE164(n.phone_number) ?? n.phone_number,
          line_type: tollFree ? 'sans_frais' : n.is_mobile ? 'cellulaire' : 'fixe',
          carrier,
          // Empty: the server keeps what it already has.
          ...(account ? { account_number: account } : {}),
          ...(pin ? { pin } : {}),
        }
      }),
  }
}

// =============================================================================
// Validation, screen by screen
// =============================================================================

interface Ctx {
  manual: boolean
  hasInvoice: boolean
  hasInk: boolean
  manualFile: File | null
  provinces: string[]
}

type Issue = { path: string; code: string }

function validate(screen: ScreenId, v: Values, ctx: Ctx): Issue[] {
  const issues: Issue[] = []
  const need = (path: keyof Values, ok: boolean, code = 'required') => {
    if (!ok) issues.push({ path, code })
  }
  const filled = (s: string) => s.trim().length > 0
  if (screen === 'numbers') {
    const seen = new Set<string>()
    v.numbers.forEach((n, i) => {
      // Extra empty lines are ignored; the first one must be filled.
      if (i > 0 && !filled(n.phone_number)) return
      const e164 = toE164(n.phone_number)
      if (!e164) issues.push({ path: `numbers.${i}.phone_number`, code: 'phone' })
      else if (seen.has(e164)) issues.push({ path: `numbers.${i}.phone_number`, code: 'duplicate' })
      if (e164) seen.add(e164)
    })
  }
  if (screen === 'carrier') {
    const anyDefault = v.numbers.some((n) => !n.custom)
    const allSet = (k: 'account_number_set') => v.numbers.filter((n) => !n.custom).every((n) => n[k])
    if (anyDefault) {
      need('carrier', filled(v.carrier))
      need('account_number', filled(v.account_number) || allSet('account_number_set'))
    }
    // No PIN check: the carrier decides whether it wants one. If it does, the team asks for a correction.
    v.numbers.forEach((n, i) => {
      if (!n.custom) return
      if (!filled(n.carrier)) issues.push({ path: `numbers.${i}.carrier`, code: 'required' })
      if (!filled(n.account_number) && !n.account_number_set)
        issues.push({ path: `numbers.${i}.account_number`, code: 'required' })
    })
  }
  if (screen === 'holder') {
    need('first_name', filled(v.first_name))
    need('last_name', filled(v.last_name))
  }
  if (screen === 'address') {
    need('address', filled(v.address))
    if (filled(v.address) && PO_BOX_RE.test(v.address)) issues.push({ path: 'address', code: 'poBox' })
    if (filled(v.address2) && PO_BOX_RE.test(v.address2)) issues.push({ path: 'address2', code: 'poBox' })
    need('city', filled(v.city))
    need('province', ctx.provinces.includes(v.province))
    need('postal_code', POSTAL_RE.test(v.postal_code.trim()), 'postal')
  }
  if (screen === 'invoice' && !ctx.hasInvoice) issues.push({ path: 'invoice', code: 'invoice' })
  if (screen === 'signer') {
    need('signer_name', filled(v.signer_name))
    need('signer_title', filled(v.signer_title))
    need('signer_email', z.string().email().safeParse(v.signer_email.trim()).success, 'email')
  }
  if (screen === 'checks') {
    need('check_owner', v.check_owner, 'check')
    need('check_matches_bill', v.check_matches_bill, 'check')
    need('check_authorize', v.check_authorize, 'check')
    if (!ctx.manual) need('check_electronic', v.check_electronic, 'check')
  }
  if (screen === 'sign') {
    if (ctx.manual && !ctx.manualFile) issues.push({ path: 'signature', code: 'file' })
    if (!ctx.manual && !ctx.hasInk) issues.push({ path: 'signature', code: 'signature' })
  }
  return issues
}

/** A server field name (API) → the screen that asks for it, and the form field. */
function screenForServerField(field: string): { screen: ScreenId; path: string } {
  const [head, index, sub] = field.split('.')
  if (head === 'numbers') {
    if (!sub || sub === 'phone_number') return { screen: 'numbers', path: index ? `numbers.${index}.phone_number` : 'numbers.0.phone_number' }
    return { screen: 'carrier', path: sub === 'pin' ? 'pin' : sub === 'carrier' ? 'carrier' : 'account_number' }
  }
  const map: Record<string, [ScreenId, string]> = {
    first_name: ['holder', 'first_name'],
    last_name: ['holder', 'last_name'],
    business_name: ['holder', 'business_name'],
    address: ['address', 'address'],
    address_po_box: ['address', 'address'],
    address2: ['address', 'address2'],
    city: ['address', 'city'],
    province: ['address', 'province'],
    province_invalid: ['address', 'province'],
    postal_code: ['address', 'postal_code'],
    postal_code_invalid: ['address', 'postal_code'],
    invoice: ['invoice', 'invoice'],
    signer_name: ['signer', 'signer_name'],
    signer_title: ['signer', 'signer_title'],
    signer_email: ['signer', 'signer_email'],
    owner: ['checks', 'check_owner'],
    matches_bill: ['checks', 'check_matches_bill'],
    authorize: ['checks', 'check_authorize'],
    electronic: ['checks', 'check_electronic'],
  }
  const [screen, path] = map[head] ?? ['sign', 'signature']
  return { screen, path }
}

// =============================================================================
// Component
// =============================================================================

interface WizardProps {
  /** Closing without sending (the draft stays). */
  onClose: () => void
  companyId: number
  /** A draft or a request to correct: the wizard picks up where it was left. */
  transfer: PortRequest | null
  options: PortOptions | undefined
  ownerEmail: string
  /** Signed and sent: the parent closes the wizard. */
  onSubmitted: (id: string) => void
}

interface Props extends WizardProps {
  open: boolean
}

/** The wizard in a window of the Numbers screen (computer). */
export function PortWizardDialog({ open, ...props }: Props) {
  // A fresh form each time the window opens (or another transfer is opened).
  if (!open) return null
  return <Wizard key={props.transfer?.id ?? 'new'} {...props} layout="dialog" />
}

/** The same wizard as a page of its own, for a phone (pages/Transfer.tsx). */
export function PortWizardPage(props: WizardProps & { closeLabel: string }) {
  return <Wizard key={props.transfer?.id ?? 'new'} {...props} layout="page" />
}

function Wizard({
  onClose,
  companyId,
  transfer,
  options,
  ownerEmail,
  onSubmitted,
  layout,
  closeLabel,
}: WizardProps & { layout: 'dialog' | 'page'; closeLabel?: string }) {
  const { t } = useTranslation()
  const { locale = 'fr' } = useParams<{ locale: string }>()
  const page = layout === 'page'
  const wide = useEstDesktop()
  // A page is for a phone: one question per screen, whatever the window width says.
  const desktop = !page && wide
  const form = useForm<Values>({ defaultValues: initialValues(transfer, options, ownerEmail) })
  const [requestId, setRequestId] = useState<string | null>(transfer?.id ?? null)
  const [current, setCurrent] = useState<PortRequest | null>(transfer)
  const [screen, setScreen] = useState<ScreenId>(() => firstScreen(transfer))
  const [manual, setManual] = useState(false)
  const [hasInk, setHasInk] = useState(false)
  const [manualFile, setManualFile] = useState<File | null>(null)
  const [invoiceError, setInvoiceError] = useState('')
  const [showAddCard, setShowAddCard] = useState(false)
  /** "Sign with my phone": the code on screen, while the computer waits. */
  const [phone, setPhone] = useState<{ id: number; code: string; createdAt: number } | null>(null)
  const pad = useRef<SignaturePadHandle>(null)

  const save = useSavePortRequest(companyId)
  const uploadInvoice = useUploadPortInvoice(companyId)
  const sign = useSignPortRequest(companyId)
  const uploadLetter = useUploadSignedLetter(companyId)
  const createCode = useCreatePhoneSignCode(companyId)
  const storeTransfer = useStorePortRequest(companyId)
  const busy = save.isPending || sign.isPending || uploadLetter.isPending || createCode.isPending
  // Read here so that setError re-renders the screens (react-hook-form subscribes on read).
  const hasErrors = Object.keys(form.formState.errors).length > 0

  const ctx: Ctx = {
    manual,
    hasInvoice: !!current?.invoice,
    hasInk,
    manualFile,
    provinces: options?.provinces ?? [],
  }
  const stepIndex = stepOf(screen)
  // On a computer a step shows all its questions; on a phone, one question per screen.
  const visible: ScreenId[] = desktop ? STEPS[stepIndex].screens : [screen]
  const lastVisible = visible[visible.length - 1]
  const isLast = lastVisible === 'sign'

  const goTo = (target: ScreenId) => {
    setScreen(desktop ? STEPS[stepOf(target)].screens[0] : target)
  }

  const applyIssues = (issues: Issue[]) => {
    issues.forEach((i) =>
      form.setError(i.path as Path<Values>, { type: i.code, message: t(`manager.numbers.port.errors.${i.code}`) }),
    )
  }

  const handleServerError = (err: unknown): boolean => {
    const e = portErrorOf(err)
    if (e.error === 'payment_method_required') {
      setShowAddCard(true)
      return true
    }
    if (e.error === 'subscription_required') {
      toast.error(t('manager.numbers.port.errors.subscription'))
      return true
    }
    if (e.error === 'demande_verrouillee') {
      toast.error(t('manager.numbers.port.errors.locked'))
      return true
    }
    if (e.fields?.length) {
      const first = screenForServerField(e.fields[0])
      goTo(first.screen)
      e.fields.forEach((f) => {
        const { path } = screenForServerField(f)
        const code = f.includes('po_box') ? 'poBox' : f.includes('postal') ? 'postal' : f === 'invoice' ? 'invoice' : e.error === 'numero_invalide' ? 'phone' : e.error === 'numero_en_double' ? 'duplicate' : 'required'
        if (path === 'invoice') setInvoiceError(t('manager.numbers.port.errors.invoice'))
        else form.setError(path as Path<Values>, { type: code, message: t(`manager.numbers.port.errors.${code}`) })
      })
      return true
    }
    return false
  }

  const persist = async (): Promise<PortRequest | null> => {
    try {
      const data = await save.mutateAsync({ id: requestId, input: toInput(form.getValues()) })
      setRequestId(data.id)
      setCurrent(data)
      return data
    } catch (err) {
      if (!handleServerError(err)) toast.error(t('manager.numbers.port.errors.generic'))
      return null
    }
  }

  const next = async () => {
    form.clearErrors()
    setInvoiceError('')
    const issues = visible.flatMap((s) => validate(s, form.getValues(), ctx))
    if (issues.length) {
      applyIssues(issues.filter((i) => i.path !== 'invoice'))
      if (issues.some((i) => i.path === 'invoice')) setInvoiceError(t('manager.numbers.port.errors.invoice'))
      return
    }
    if (isLast) {
      await submit()
      return
    }
    // The invoice is sent as soon as it is chosen; the other answers are saved on the way.
    if (!visible.includes('invoice')) {
      const saved = await persist()
      if (!saved) return
    }
    const idx = SCREENS.indexOf(lastVisible)
    setScreen(SCREENS[idx + 1])
  }

  const back = () => {
    form.clearErrors()
    setPhone(null)
    const first = visible[0]
    const idx = SCREENS.indexOf(first)
    if (idx <= 0) return
    const prev = SCREENS[idx - 1]
    setScreen(desktop ? STEPS[stepOf(prev)].screens[0] : prev)
  }

  const checks = () => {
    const v = form.getValues()
    return {
      owner: v.check_owner,
      matches_bill: v.check_matches_bill,
      authorize: v.check_authorize,
      ...(manual ? {} : { electronic: v.check_electronic }),
    }
  }

  const submit = async () => {
    const saved = await persist()
    if (!saved) return
    try {
      if (manual && manualFile) {
        await uploadLetter.mutateAsync({ id: saved.id, file: manualFile, checks: checks() })
      } else {
        const signature = pad.current?.export()
        if (!signature) {
          applyIssues([{ path: 'signature', code: 'signature' }])
          return
        }
        await sign.mutateAsync({
          id: saved.id,
          checks: checks(),
          signaturePng: signature.png,
          signaturePoints: signature.points,
        })
      }
      toast.success(t('manager.numbers.port.submitted'))
      // The parent closes the wizard and shows the transfer's page.
      onSubmitted(saved.id)
    } catch (err) {
      if (!handleServerError(err)) toast.error(t('manager.numbers.port.errors.generic'))
    }
  }

  // The same answers as for signing here are checked and saved, then the server gives a code.
  const startPhone = async () => {
    form.clearErrors()
    const issues = (['signer', 'checks'] as ScreenId[]).flatMap((s) => validate(s, form.getValues(), ctx))
    if (issues.length) {
      applyIssues(issues)
      return
    }
    const saved = await persist()
    if (!saved) return
    try {
      const data = await createCode.mutateAsync({ id: saved.id, checks: checks() })
      setPhone({ id: data.id, code: data.code ?? '', createdAt: Date.now() })
    } catch (err) {
      if (!handleServerError(err)) toast.error(t('manager.numbers.port.qr.error'))
    }
  }

  const phoneSigned = (signed: PortRequest) => {
    storeTransfer(signed)
    toast.success(t('manager.numbers.port.submitted'))
    onSubmitted(signed.id)
  }

  const chooseInvoice = async (file: File | undefined) => {
    setInvoiceError('')
    if (!file) return
    const problem = fileProblem(file)
    if (problem) {
      setInvoiceError(t(`manager.numbers.port.errors.${problem}`))
      return
    }
    const saved = requestId ? current : await persist()
    if (!saved) return
    try {
      const data = await uploadInvoice.mutateAsync({ id: saved.id, file })
      setCurrent(data)
    } catch (err) {
      const e = portErrorOf(err)
      setInvoiceError(
        t(
          `manager.numbers.port.errors.${e.error === 'fichier_format' ? 'fileFormat' : e.error === 'fichier_trop_gros' ? 'fileSize' : 'generic'}`,
        ),
      )
    }
  }

  const downloadLetter = async () => {
    const saved = await persist()
    if (!saved) return
    try {
      await downloadPortLetter(companyId, saved.id)
      setManual(true)
    } catch (err) {
      if (!handleServerError(err)) toast.error(t('manager.numbers.port.errors.generic'))
    }
  }

  const close = () => {
    if (!busy) onClose()
  }

  // Outside a dialog, plain headings: Radix titles only live inside a Dialog.
  const Title: ElementType = page ? 'h1' : DialogTitle
  const Description: ElementType = page ? 'p' : DialogDescription
  const atStart = SCREENS.indexOf(visible[0]) === 0

  const inner = (
    <>
      {/* Header */}
      <div className="flex items-start justify-between gap-4 px-5 sm:px-6 pt-5 pb-4 border-b border-slate-200 dark:border-slate-800">
        <div className="min-w-0">
          <Title className="text-lg font-semibold leading-tight">{t('manager.numbers.port.title')}</Title>
          <Description className="mt-1 text-sm text-slate-500 dark:text-slate-400">
            {t('manager.numbers.port.stepOf', { n: stepIndex + 1, total: STEPS.length })} ·{' '}
            {t(`manager.numbers.port.steps.${STEPS[stepIndex].key}`)}
          </Description>
        </div>
        <Button type="button" variant="ghost" size="sm" onClick={close} disabled={busy}>
          {closeLabel ?? t('manager.numbers.port.close')}
        </Button>
      </div>

      {desktop ? (
        <ol className="flex gap-2 px-6 py-3 border-b border-slate-100 dark:border-slate-800/60">
          {STEPS.map((s, i) => (
            <li
              key={s.key}
              className={cn(
                'flex items-center gap-2 text-xs font-medium',
                i === stepIndex ? 'text-slate-900 dark:text-white' : 'text-slate-400',
              )}
            >
              <span
                className={cn(
                  'inline-flex h-5 w-5 items-center justify-center rounded-full text-[11px]',
                  i < stepIndex
                    ? 'bg-emerald-500 text-white'
                    : i === stepIndex
                      ? 'bg-blue-500 text-white'
                      : 'bg-slate-100 dark:bg-slate-800',
                )}
              >
                {i < stepIndex ? <Check className="h-3 w-3" /> : i + 1}
              </span>
              {t(`manager.numbers.port.steps.${s.key}`)}
              {i < STEPS.length - 1 && <span className="w-6 h-px bg-slate-200 dark:bg-slate-700 ml-1" />}
            </li>
          ))}
        </ol>
      ) : (
        <div className="h-1 bg-slate-100 dark:bg-slate-800">
          <div
            className="h-1 bg-blue-500 transition-all"
            style={{ width: `${((SCREENS.indexOf(screen) + 1) / SCREENS.length) * 100}%` }}
          />
        </div>
      )}

      {/* Body */}
      <div className="flex-1 min-h-0 overflow-y-auto px-5 sm:px-6 py-5 space-y-8">
        {current?.status === 'a_corriger' && current.correction_message && (
          <div className="rounded-lg border border-amber-200 bg-amber-50 dark:border-amber-900/50 dark:bg-amber-900/20 px-4 py-3 text-sm text-amber-900 dark:text-amber-200">
            {t('manager.numbers.port.correctionBanner', { message: current.correction_message })}
          </div>
        )}
        {visible.map((s) => (
          // While the code is on screen, the answers it was made from stay as they are.
          <fieldset
            key={s}
            disabled={!!phone && s !== 'sign'}
            className={cn('min-w-0 space-y-4', phone && s !== 'sign' && 'opacity-60')}
          >
            {s === 'numbers' && <NumbersScreen form={form} options={options} big={!desktop} />}
            {s === 'carrier' && <CarrierScreen form={form} big={!desktop} />}
            {s === 'holder' && <HolderScreen form={form} big={!desktop} />}
            {s === 'address' && <AddressScreen form={form} options={options} big={!desktop} locale={locale} />}
            {s === 'invoice' && (
              <InvoiceScreen
                big={!desktop}
                mobile={!desktop}
                invoice={current?.invoice ?? null}
                uploading={uploadInvoice.isPending}
                error={invoiceError}
                onChoose={(f) => void chooseInvoice(f)}
              />
            )}
            {s === 'signer' && <SignerScreen form={form} big={!desktop} />}
            {s === 'checks' && (
              <ChecksScreen
                form={form}
                big={!desktop}
                manual={manual}
                onManual={() => void downloadLetter()}
                onElectronic={() => setManual(false)}
                downloading={save.isPending}
              />
            )}
            {s === 'sign' && (
              <SignScreen
                form={form}
                big={!desktop}
                manual={manual}
                mobile={!desktop}
                padRef={pad}
                tallPad={page}
                onInk={setHasInk}
                manualFile={manualFile}
                onManualFile={(f) => {
                  form.clearErrors('signature' as Path<Values>)
                  const problem = f ? fileProblem(f) : null
                  if (problem) {
                    form.setError('signature' as Path<Values>, {
                      message: t(`manager.numbers.port.errors.${problem}`),
                    })
                    return
                  }
                  setManualFile(f ?? null)
                }}
                onDownload={() => void downloadLetter()}
                onElectronic={() => setManual(false)}
                onPhone={desktop && !manual ? () => void startPhone() : undefined}
                phoneBusy={createCode.isPending}
                phonePanel={
                  phone && requestId ? (
                    <PhoneSignPanel
                      key={phone.id}
                      companyId={companyId}
                      transferId={requestId}
                      codeId={phone.id}
                      code={phone.code}
                      createdAt={phone.createdAt}
                      locale={locale}
                      renewing={createCode.isPending}
                      onRenew={() => void startPhone()}
                      onCancel={() => {
                        setPhone(null)
                        setHasInk(false)
                      }}
                      onSigned={phoneSigned}
                    />
                  ) : null
                }
              />
            )}
          </fieldset>
        ))}
      </div>

      {/* Footer */}
      {/* In a narrow window both buttons sit on the right: the help bubble covers the bottom left corner.
          On the phone page (no bubble there), a large Continue button across the screen. */}
      <div
        className={cn(
          'flex flex-wrap items-center gap-3 px-5 sm:px-6 py-4 border-t border-slate-200 dark:border-slate-800 pb-[max(1rem,env(safe-area-inset-bottom))]',
          desktop ? 'justify-between' : page ? 'flex-nowrap' : 'justify-end',
          page && hasErrors && 'flex-wrap',
        )}
      >
        {hasErrors && (
          <p className="w-full text-xs text-red-600 dark:text-red-400">{t('manager.numbers.port.errors.review')}</p>
        )}
        {!(page && atStart) && (
          <Button
            type="button"
            variant="outline"
            onClick={back}
            disabled={busy || atStart}
            className={cn(page && 'h-12 px-5 text-base')}
          >
            {t('manager.numbers.port.back')}
          </Button>
        )}
        <Button
          type="button"
          onClick={() => void next()}
          disabled={busy || uploadInvoice.isPending || !!phone}
          className={cn(
            'bg-blue-500 hover:bg-blue-600 text-white',
            page ? 'flex-1 h-12 text-base font-semibold' : 'min-w-[9rem]',
          )}
        >
          {busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
          {isLast
            ? manual
              ? t('manager.numbers.port.sign.manualSubmit')
              : t('manager.numbers.port.sign.submit')
            : t('manager.numbers.port.continue')}
        </Button>
      </div>
    </>
  )

  return (
    <>
      {page ? (
        <div className="flex flex-col h-[100dvh] bg-background text-foreground">{inner}</div>
      ) : (
        <Dialog open onOpenChange={(o) => !o && close()}>
          <DialogContent
            showCloseButton={false}
            onInteractOutside={(e) => e.preventDefault()}
            className={cn(
              'p-0 gap-0 flex flex-col overflow-hidden',
              desktop
                ? 'sm:max-w-3xl max-h-[90vh]'
                : 'top-0 left-0 translate-x-0 translate-y-0 w-full max-w-full sm:max-w-full h-[100dvh] rounded-none border-0',
            )}
          >
            {inner}
          </DialogContent>
        </Dialog>
      )}

      <UpdateCardDialog
        open={showAddCard}
        onClose={() => setShowAddCard(false)}
        description={t('manager.numbers.marketplace.addCardDescription')}
      />
    </>
  )
}

/** Where to pick up a request: the first screen still missing something. */
function firstScreen(transfer: PortRequest | null): ScreenId {
  if (!transfer) return 'numbers'
  if (transfer.status === 'a_corriger') return 'numbers'
  const order = transfer.missing.map((f) => screenForServerField(f).screen)
  const firstMissing = SCREENS.find((s) => order.includes(s))
  return firstMissing ?? 'signer'
}

function fileProblem(file: File): 'fileFormat' | 'fileSize' | null {
  if (!['application/pdf', 'image/jpeg', 'image/png'].includes(file.type)) return 'fileFormat'
  if (file.size > 10 * 1024 * 1024) return 'fileSize'
  return null
}

// =============================================================================
// Screens
// =============================================================================

type FormProps = { form: UseFormReturn<Values>; big: boolean }

function Question({ title, help, big }: { title: string; help?: string; big: boolean }) {
  return (
    <div>
      <h3 className={cn('font-semibold text-slate-900 dark:text-white', big ? 'text-xl leading-snug' : 'text-base')}>
        {title}
      </h3>
      {help && <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">{help}</p>}
    </div>
  )
}

function Field({
  label,
  help,
  error,
  children,
  htmlFor,
}: {
  label: string
  help?: string
  error?: string
  children: React.ReactNode
  htmlFor?: string
}) {
  return (
    <div className="space-y-1.5">
      <label htmlFor={htmlFor} className="block text-sm font-medium text-slate-700 dark:text-slate-300">
        {label}
      </label>
      {children}
      {error ? (
        <p className="text-xs text-red-600 dark:text-red-400">{error}</p>
      ) : (
        help && <p className="text-xs text-slate-500 dark:text-slate-400">{help}</p>
      )}
    </div>
  )
}

const selectClass =
  'h-10 w-full rounded-md border border-input bg-transparent px-3 text-sm shadow-xs focus:outline-none focus:ring-2 focus:ring-blue-500/40 dark:bg-input/30'

function errorAt(form: UseFormReturn<Values>, path: string): string | undefined {
  const parts = path.split('.')
  let node: unknown = form.formState.errors
  for (const p of parts) node = (node as Record<string, unknown> | undefined)?.[p]
  return (node as { message?: string } | undefined)?.message
}

function NumbersScreen({ form, options, big }: FormProps & { options: PortOptions | undefined }) {
  const { t } = useTranslation()
  const { fields, append, remove } = useFieldArray({ control: form.control, name: 'numbers' })
  const rows = form.watch('numbers')
  const scope = form.watch('scope')
  const temporaries = options?.temporary_numbers ?? []
  return (
    <>
      <Question title={t('manager.numbers.port.numbers.title')} help={t('manager.numbers.port.numbers.help')} big={big} />
      <div className="space-y-3">
        {fields.map((f, i) => {
          const tollFree = isTollFree(rows[i]?.phone_number ?? '')
          const error = errorAt(form, `numbers.${i}.phone_number`)
          return (
            <div key={f.id} className="space-y-1">
              <div className="flex flex-wrap items-center gap-2 sm:gap-3">
                <Input
                  {...form.register(`numbers.${i}.phone_number`, {
                    onBlur: (e) => form.setValue(`numbers.${i}.phone_number`, formatNanp(e.target.value)),
                  })}
                  type="tel"
                  inputMode="tel"
                  autoComplete="off"
                  placeholder={t('manager.numbers.port.numbers.placeholder')}
                  aria-invalid={!!error}
                  className="h-11 flex-1 min-w-[12rem] tabular-nums"
                />
                {!tollFree && (
                  <label className="inline-flex items-center gap-2 text-sm text-slate-700 dark:text-slate-300 h-11 px-1">
                    <input type="checkbox" className="h-4 w-4 accent-blue-500" {...form.register(`numbers.${i}.is_mobile`)} />
                    {t('manager.numbers.port.numbers.mobile')}
                  </label>
                )}
                {fields.length > 1 && (
                  <Button type="button" variant="ghost" size="sm" onClick={() => remove(i)}>
                    {t('manager.numbers.port.numbers.remove')}
                  </Button>
                )}
              </div>
              {error && <p className="text-xs text-red-600 dark:text-red-400">{error}</p>}
            </div>
          )
        })}
        <Button type="button" variant="outline" size="sm" onClick={() => append(emptyRow())}>
          <Plus className="h-4 w-4 mr-1.5" />
          {t('manager.numbers.port.numbers.add')}
        </Button>
      </div>

      <fieldset className="space-y-2 pt-2">
        <legend className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
          {t('manager.numbers.port.numbers.scopeTitle')}
        </legend>
        {(['tous', 'partie'] as const).map((value) => (
          <label
            key={value}
            className={cn(
              'flex items-center gap-3 rounded-lg border px-3 py-2.5 text-sm cursor-pointer',
              scope === value
                ? 'border-blue-500 bg-blue-50/50 dark:bg-blue-500/10'
                : 'border-slate-200 dark:border-slate-700',
            )}
          >
            <input type="radio" value={value} className="accent-blue-500" {...form.register('scope')} />
            {t(`manager.numbers.port.numbers.scope.${value}`)}
          </label>
        ))}
        {scope === 'partie' && (
          <p className="text-xs text-slate-500 dark:text-slate-400">{t('manager.numbers.port.numbers.scopePartHelp')}</p>
        )}
      </fieldset>

      {temporaries.length > 1 ? (
        <Field label={t('manager.numbers.port.numbers.temporaryTitle')} help={t('manager.numbers.port.numbers.temporaryHelp')}>
          <select
            className={selectClass}
            value={form.watch('temporary_number_id') ?? ''}
            onChange={(e) => form.setValue('temporary_number_id', e.target.value ? Number(e.target.value) : null)}
          >
            {temporaries.map((n) => (
              <option key={n.id} value={n.id}>
                {formatNanp(n.phone_number)}
                {n.label ? ` · ${n.label}` : ''}
              </option>
            ))}
          </select>
        </Field>
      ) : (
        temporaries.length === 1 && (
          <p className="flex items-start gap-2 text-xs text-slate-500 dark:text-slate-400">
            <Info className="h-4 w-4 shrink-0 mt-px" />
            {t('manager.numbers.port.numbers.temporaryOne', { number: formatNanp(temporaries[0].phone_number) })}
          </p>
        )
      )}
    </>
  )
}

function CarrierScreen({ form, big }: FormProps) {
  const { t } = useTranslation()
  const rows = form.watch('numbers')
  const defaultsSaved = rows.filter((n) => !n.custom)
  const accountSaved = defaultsSaved.length > 0 && defaultsSaved.every((n) => n.account_number_set)
  const pinSaved = defaultsSaved.length > 0 && defaultsSaved.every((n) => n.pin_set)
  const several = rows.filter((n) => n.phone_number.trim()).length > 1
  return (
    <>
      <Question title={t('manager.numbers.port.carrier.title')} help={t('manager.numbers.port.carrier.help')} big={big} />
      {rows.some((n) => !n.custom) && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Field label={t('manager.numbers.port.carrier.name')} error={errorAt(form, 'carrier')} htmlFor="port-carrier">
            <Input id="port-carrier" {...form.register('carrier')} placeholder={t('manager.numbers.port.carrier.namePlaceholder')} className="h-11" />
          </Field>
          <Field label={t('manager.numbers.port.carrier.account')} error={errorAt(form, 'account_number')} htmlFor="port-account">
            <Input
              id="port-account"
              {...form.register('account_number')}
              autoComplete="off"
              placeholder={accountSaved ? t('manager.numbers.port.carrier.saved') : ''}
              className="h-11 ph-mask"
            />
          </Field>
          <Field
            label={t('manager.numbers.port.carrier.pin')}
            help={t('manager.numbers.port.carrier.pinHelp')}
            error={errorAt(form, 'pin')}
            htmlFor="port-pin"
          >
            <Input
              id="port-pin"
              {...form.register('pin')}
              autoComplete="off"
              inputMode="numeric"
              placeholder={pinSaved ? t('manager.numbers.port.carrier.saved') : ''}
              className="h-11 ph-mask"
            />
          </Field>
        </div>
      )}
      {several && (
        <div className="space-y-3 pt-1">
          <p className="text-sm font-medium text-slate-700 dark:text-slate-300">
            {t('manager.numbers.port.carrier.perNumberTitle')}
          </p>
          {rows.map((n, i) =>
            n.phone_number.trim() ? (
              <div key={i} className="rounded-lg border border-slate-200 dark:border-slate-700 px-3 py-2.5 space-y-3">
                <label className="flex items-center justify-between gap-3 text-sm">
                  <span className="font-medium tabular-nums text-slate-900 dark:text-white">{formatNanp(n.phone_number)}</span>
                  <span className="inline-flex items-center gap-2 text-slate-600 dark:text-slate-300">
                    <input type="checkbox" className="h-4 w-4 accent-blue-500" {...form.register(`numbers.${i}.custom`)} />
                    {t('manager.numbers.port.carrier.perNumber')}
                  </span>
                </label>
                {n.custom && (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <Field label={t('manager.numbers.port.carrier.name')} error={errorAt(form, `numbers.${i}.carrier`)}>
                      <Input {...form.register(`numbers.${i}.carrier`)} className="h-10" />
                    </Field>
                    <Field label={t('manager.numbers.port.carrier.account')} error={errorAt(form, `numbers.${i}.account_number`)}>
                      <Input
                        {...form.register(`numbers.${i}.account_number`)}
                        autoComplete="off"
                        placeholder={n.account_number_set ? t('manager.numbers.port.carrier.saved') : ''}
                        className="h-10 ph-mask"
                      />
                    </Field>
                    <Field label={t('manager.numbers.port.carrier.pin')} error={errorAt(form, `numbers.${i}.pin`)}>
                      <Input
                        {...form.register(`numbers.${i}.pin`)}
                        autoComplete="off"
                        inputMode="numeric"
                        placeholder={n.pin_set ? t('manager.numbers.port.carrier.saved') : ''}
                        className="h-10 ph-mask"
                      />
                    </Field>
                  </div>
                )}
              </div>
            ) : null,
          )}
        </div>
      )}
    </>
  )
}

function HolderScreen({ form, big }: FormProps) {
  const { t } = useTranslation()
  return (
    <>
      <Question title={t('manager.numbers.port.holder.title')} help={t('manager.numbers.port.holder.help')} big={big} />
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Field label={t('manager.numbers.port.holder.firstName')} error={errorAt(form, 'first_name')} htmlFor="port-first">
          <Input id="port-first" {...form.register('first_name')} autoComplete="given-name" className="h-11" />
        </Field>
        <Field label={t('manager.numbers.port.holder.lastName')} error={errorAt(form, 'last_name')} htmlFor="port-last">
          <Input id="port-last" {...form.register('last_name')} autoComplete="family-name" className="h-11" />
        </Field>
      </div>
      <Field label={t('manager.numbers.port.holder.business')} help={t('manager.numbers.port.holder.businessHelp')} htmlFor="port-business">
        <Input id="port-business" {...form.register('business_name')} autoComplete="organization" className="h-11" />
      </Field>
    </>
  )
}

function AddressScreen({ form, options, big, locale }: FormProps & { options: PortOptions | undefined; locale: string }) {
  const { t } = useTranslation()
  const address = form.watch('address')
  return (
    <>
      <Question title={t('manager.numbers.port.address.title')} help={t('manager.numbers.port.address.help')} big={big} />
      <Field label={t('manager.numbers.port.address.address')} error={errorAt(form, 'address')} htmlFor="port-address">
        <GoogleAddressAutocomplete
          id="port-address"
          value={address}
          onChange={(v) => form.setValue('address', v)}
          onPlaceSelected={({ address1, city, province, postalCode }) => {
            form.setValue('address', address1)
            if (city) form.setValue('city', city)
            if (province && options?.provinces.includes(province)) form.setValue('province', province)
            if (postalCode) form.setValue('postal_code', formatPostal(postalCode))
          }}
          ariaInvalid={!!errorAt(form, 'address')}
        />
      </Field>
      {/* The unit on its own line: the address suggestion never carries it. */}
      <Field label={t('manager.numbers.port.address.address2')} help={t('manager.numbers.port.address.address2Help')}
        error={errorAt(form, 'address2')} htmlFor="port-address2">
        <Input id="port-address2" {...form.register('address2')} autoComplete="address-line2" maxLength={100} className="h-11" />
      </Field>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <Field label={t('manager.numbers.port.address.city')} error={errorAt(form, 'city')} htmlFor="port-city">
          <Input id="port-city" {...form.register('city')} autoComplete="address-level2" className="h-11" />
        </Field>
        <Field label={t('manager.numbers.port.address.province')} error={errorAt(form, 'province')} htmlFor="port-province">
          <select id="port-province" {...form.register('province')} className={cn(selectClass, 'h-11')}>
            <option value="">{t('manager.numbers.port.address.provincePlaceholder')}</option>
            {(options?.provinces ?? []).map((p) => (
              <option key={p} value={p}>
                {provinceLabel(p, locale)}
              </option>
            ))}
          </select>
        </Field>
        <Field label={t('manager.numbers.port.address.postal')} error={errorAt(form, 'postal_code')} htmlFor="port-postal">
          <Input
            id="port-postal"
            {...form.register('postal_code', {
              onBlur: (e) => form.setValue('postal_code', formatPostal(e.target.value)),
            })}
            autoComplete="postal-code"
            maxLength={7}
            className="h-11 uppercase"
          />
        </Field>
      </div>
    </>
  )
}

function InvoiceScreen({
  big,
  mobile,
  invoice,
  uploading,
  error,
  onChoose,
}: {
  big: boolean
  mobile: boolean
  invoice: PortRequest['invoice']
  uploading: boolean
  error: string
  onChoose: (file: File | undefined) => void
}) {
  const { t } = useTranslation()
  const fileInput = useRef<HTMLInputElement>(null)
  const photoInput = useRef<HTMLInputElement>(null)
  const [dragging, setDragging] = useState(false)
  return (
    <>
      <Question title={t('manager.numbers.port.invoice.title')} help={t('manager.numbers.port.invoice.help')} big={big} />
      <div
        onDragOver={(e) => {
          e.preventDefault()
          setDragging(true)
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={(e) => {
          e.preventDefault()
          setDragging(false)
          onChoose(e.dataTransfer.files?.[0])
        }}
        className={cn(
          'rounded-xl border-2 border-dashed px-5 py-8 text-center space-y-4',
          dragging ? 'border-blue-500 bg-blue-50/50 dark:bg-blue-500/10' : 'border-slate-300 dark:border-slate-700',
        )}
      >
        {invoice ? (
          <div className="flex items-center justify-center gap-2 text-sm text-slate-700 dark:text-slate-200">
            <FileText className="h-5 w-5 text-emerald-600" />
            <span className="font-medium truncate max-w-[16rem]">{invoice.name}</span>
            <span className="text-emerald-700 dark:text-emerald-400">· {t('manager.numbers.port.invoice.added')}</span>
          </div>
        ) : (
          <Upload className="h-8 w-8 text-slate-300 mx-auto" />
        )}
        <div className={cn('flex gap-2 justify-center', mobile && 'flex-col')}>
          {mobile && (
            <Button type="button" onClick={() => photoInput.current?.click()} disabled={uploading} className="bg-blue-500 hover:bg-blue-600 text-white">
              <Camera className="h-4 w-4 mr-2" />
              {t('manager.numbers.port.invoice.photo')}
            </Button>
          )}
          <Button type="button" variant="outline" onClick={() => fileInput.current?.click()} disabled={uploading}>
            {uploading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Upload className="h-4 w-4 mr-2" />}
            {uploading
              ? t('manager.numbers.port.invoice.uploading')
              : invoice
                ? t('manager.numbers.port.invoice.replace')
                : t('manager.numbers.port.invoice.choose')}
          </Button>
        </div>
        <p className="text-xs text-slate-500 dark:text-slate-400">{t('manager.numbers.port.invoice.formats')}</p>
        <input
          ref={fileInput}
          type="file"
          accept="application/pdf,image/jpeg,image/png"
          className="hidden"
          onChange={(e) => {
            onChoose(e.target.files?.[0])
            e.target.value = ''
          }}
        />
        <input
          ref={photoInput}
          type="file"
          accept="image/jpeg,image/png"
          capture="environment"
          className="hidden"
          onChange={(e) => {
            onChoose(e.target.files?.[0])
            e.target.value = ''
          }}
        />
      </div>
      {error && <p className="text-sm text-red-600 dark:text-red-400">{error}</p>}
    </>
  )
}

function SignerScreen({ form, big }: FormProps) {
  const { t } = useTranslation()
  useEffect(() => {
    // The holder signs most of the time: start from their name.
    const v = form.getValues()
    if (!v.signer_name.trim()) form.setValue('signer_name', `${v.first_name} ${v.last_name}`.trim())
  }, [form])
  return (
    <>
      {/* The carrier sets the date: no choice here, only what to expect. */}
      <p className="flex items-start gap-2 rounded-lg bg-slate-50 dark:bg-slate-800/60 px-3 py-2.5 text-sm leading-relaxed text-slate-700 dark:text-slate-200">
        <Info className="h-4 w-4 shrink-0 mt-0.5 text-blue-500" />
        {t('manager.numbers.port.timing')}
      </p>
      <Question title={t('manager.numbers.port.signer.title')} help={t('manager.numbers.port.signer.help')} big={big} />
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Field label={t('manager.numbers.port.signer.name')} help={t('manager.numbers.port.signer.nameHelp')} error={errorAt(form, 'signer_name')} htmlFor="port-signer">
          <Input id="port-signer" {...form.register('signer_name')} autoComplete="name" className="h-11" />
        </Field>
        <Field label={t('manager.numbers.port.signer.role')} error={errorAt(form, 'signer_title')} htmlFor="port-role">
          <Input id="port-role" {...form.register('signer_title')} autoComplete="organization-title" placeholder={t('manager.numbers.port.signer.rolePlaceholder')} className="h-11" />
        </Field>
      </div>
      <Field label={t('manager.numbers.port.signer.email')} help={t('manager.numbers.port.signer.emailHelp')} error={errorAt(form, 'signer_email')} htmlFor="port-email">
        <Input id="port-email" type="email" {...form.register('signer_email')} autoComplete="email" className="h-11" />
      </Field>
    </>
  )
}

function ChecksScreen({
  form,
  big,
  manual,
  onManual,
  onElectronic,
  downloading,
}: FormProps & { manual: boolean; onManual: () => void; onElectronic: () => void; downloading: boolean }) {
  const { t } = useTranslation()
  const boxes: { name: 'check_owner' | 'check_matches_bill' | 'check_authorize' | 'check_electronic'; key: string }[] = [
    { name: 'check_owner', key: 'owner' },
    { name: 'check_matches_bill', key: 'matchesBill' },
    { name: 'check_authorize', key: 'authorize' },
    ...(manual ? [] : [{ name: 'check_electronic' as const, key: 'electronic' }]),
  ]
  const anyError = boxes.some((b) => errorAt(form, b.name))
  return (
    <>
      <Question title={t('manager.numbers.port.checks.title')} big={big} />
      <div className="space-y-2">
        {boxes.map((b) => (
          <label
            key={b.name}
            className={cn(
              'flex items-start gap-3 rounded-lg border px-3 py-3 text-sm leading-snug cursor-pointer',
              errorAt(form, b.name) ? 'border-red-300 dark:border-red-800' : 'border-slate-200 dark:border-slate-700',
            )}
          >
            <input type="checkbox" className="h-4 w-4 mt-0.5 shrink-0 accent-blue-500" {...form.register(b.name)} />
            <span className="text-slate-700 dark:text-slate-200">{t(`manager.numbers.port.checks.${b.key}`)}</span>
          </label>
        ))}
        {anyError && <p className="text-xs text-red-600 dark:text-red-400">{t('manager.numbers.port.errors.check')}</p>}
      </div>
      <p className="flex items-start gap-2 rounded-lg bg-slate-50 dark:bg-slate-800/60 px-3 py-2.5 text-sm text-slate-700 dark:text-slate-200">
        <Info className="h-4 w-4 shrink-0 mt-0.5 text-blue-500" />
        {t('manager.numbers.port.checks.keepService')}
      </p>
      <div className="text-sm">
        {manual ? (
          <button type="button" onClick={onElectronic} className="font-medium text-blue-600 hover:underline dark:text-blue-400">
            {t('manager.numbers.port.checks.electronicLink')}
          </button>
        ) : (
          <button
            type="button"
            onClick={onManual}
            disabled={downloading}
            className="font-medium text-blue-600 hover:underline dark:text-blue-400 disabled:opacity-50"
          >
            {t('manager.numbers.port.checks.manualLink')}
          </button>
        )}
      </div>
    </>
  )
}

function SignScreen({
  form,
  big,
  manual,
  mobile,
  padRef,
  tallPad,
  onInk,
  manualFile,
  onManualFile,
  onDownload,
  onElectronic,
  onPhone,
  phoneBusy,
  phonePanel,
}: FormProps & {
  manual: boolean
  mobile: boolean
  padRef: React.RefObject<SignaturePadHandle | null>
  /** Phone page: the signature area takes most of the screen. */
  tallPad: boolean
  onInk: (v: boolean) => void
  manualFile: File | null
  onManualFile: (f: File | undefined) => void
  onDownload: () => void
  onElectronic: () => void
  /** Computer only: sign with a finger on the phone instead of the mouse. */
  onPhone?: () => void
  phoneBusy: boolean
  /** The QR code and the wait, in place of the signature area. */
  phonePanel: React.ReactNode
}) {
  const { t } = useTranslation()
  const input = useRef<HTMLInputElement>(null)
  const error = errorAt(form, 'signature')
  if (manual) {
    return (
      <>
        <Question title={t('manager.numbers.port.sign.manualTitle')} help={t('manager.numbers.port.sign.manualHelp')} big={big} />
        <div className={cn('flex gap-2', mobile && 'flex-col')}>
          <Button type="button" variant="outline" onClick={onDownload}>
            <FileText className="h-4 w-4 mr-2" />
            {t('manager.numbers.port.sign.download')}
          </Button>
          <Button type="button" variant="outline" onClick={() => input.current?.click()}>
            <Upload className="h-4 w-4 mr-2" />
            {manualFile ? manualFile.name : t('manager.numbers.port.sign.manualChoose')}
          </Button>
        </div>
        <input
          ref={input}
          type="file"
          accept="application/pdf,image/jpeg,image/png"
          className="hidden"
          onChange={(e) => {
            onManualFile(e.target.files?.[0])
            e.target.value = ''
          }}
        />
        {error && <p className="text-xs text-red-600 dark:text-red-400">{error}</p>}
        <button type="button" onClick={onElectronic} className="text-sm font-medium text-blue-600 hover:underline dark:text-blue-400">
          {t('manager.numbers.port.checks.electronicLink')}
        </button>
      </>
    )
  }
  return (
    <>
      <Question
        title={t('manager.numbers.port.sign.title')}
        help={phonePanel ? undefined : t('manager.numbers.port.sign.help')}
        big={big}
      />
      {phonePanel ?? (
        <>
          {onPhone && (
            <Button type="button" variant="outline" onClick={onPhone} disabled={phoneBusy}>
              {phoneBusy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Smartphone className="h-4 w-4 mr-2" />}
              {t('manager.numbers.port.qr.button')}
            </Button>
          )}
          <SignaturePad ref={padRef} onInkChange={onInk} tall={tallPad} />
          {error && <p className="text-xs text-red-600 dark:text-red-400">{error}</p>}
        </>
      )}
      <p className="text-xs text-slate-500 dark:text-slate-400">{t('manager.numbers.port.sign.legal')}</p>
      <button type="button" onClick={onDownload} className="text-sm font-medium text-blue-600 hover:underline dark:text-blue-400">
        {t('manager.numbers.port.sign.download')}
      </button>
    </>
  )
}
